import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { boardAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';

// 스타일드 컴포넌트
const BoardListContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 0;
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.5rem;
    text-align: center;
  }
`;

const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const BoardCard = styled(Link)`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
  text-decoration: none;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const BoardCardTitle = styled.h2`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const BoardCardDescription = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 1rem;
  line-height: 1.4;
  flex-grow: 1;
`;

const BoardCardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const NoBoards = styled.div`
  text-align: center;
  padding: 3rem 0;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  width: 100%;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

interface Board {
  board_id: number;
  name: string;
  slug: string;
  description: string;
  is_active: number;
  permissions: BoardPermission[];
  created_at: string;
}

interface BoardPermission {
  role_id: number;
  can_read: number;
  can_write: number;
}

const BoardList: React.FC = () => {
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        setLoading(true);
        const response = await boardAPI.getBoards();
        
        // 서버 응답에서 boards 배열을 추출
        const boardsData = response.data.boards || [];
        
        // 사용자 역할 ID (기본값: 게스트)
        const userRoleId = user?.roleId || 1;
        
        // 권한에 맞는 게시판만 필터링 (권한이 있고 활성화된 게시판)
        const accessibleBoards = boardsData.filter((board: Board) => {
          // 게시판이 활성화되어 있는지 확인
          if (board.is_active !== 1) {
            return false;
          }
          
          // 관리자는 모든 게시판에 접근 가능
          if (userRoleId === 4) {
            return true;
          }
          
          // 사용자 역할에 맞는 권한이 있는지 확인 (읽기 권한)
          return board.permissions?.some(perm => 
            perm.role_id === userRoleId && perm.can_read === 1
          );
        });
        
        setBoards(accessibleBoards);
        setError(null);
      } catch (err) {
        console.error('게시판 목록 불러오기 오류:', err);
        setError('게시판 목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBoards();
  }, [user]);
  
  if (loading) {
    return (
      <BoardListContainer>
        <LoadingWrapper>게시판 목록을 불러오는 중입니다...</LoadingWrapper>
      </BoardListContainer>
    );
  }
  
  if (error) {
    return (
      <BoardListContainer>
        <NoBoards>{error}</NoBoards>
      </BoardListContainer>
    );
  }
  
  return (
    <BoardListContainer>
      <PageTitle>게시판 목록</PageTitle>
      
      {boards.length === 0 ? (
        <NoBoards>표시할 게시판이 없습니다.</NoBoards>
      ) : (
        <BoardGrid>
          {boards.map(board => (
            <BoardCard key={board.board_id} to={`/boards/${board.board_id}`}>
              <BoardCardTitle>{board.name}</BoardCardTitle>
              <BoardCardDescription>
                {board.description || '게시판 설명이 없습니다.'}
              </BoardCardDescription>
              <BoardCardFooter>
                <span>게시판 ID: {board.board_id}</span>
              </BoardCardFooter>
            </BoardCard>
          ))}
        </BoardGrid>
      )}
    </BoardListContainer>
  );
};

export default BoardList; 